import { createSelector } from '@reduxjs/toolkit';
import invariant from 'invariant';

import { DonationPageSectionsType, DonationPageTabsEnum } from 'src/models/DonationPage/Section';
import {
  DonationPageGlobalStoreType,
  DonationPageMainSectionStoreType,
  DonationPageOptionsSectionStoreType,
  DonationPageCheckoutSectionStoreType,
} from 'src/models/DonationPage/Store';
import { DonationPageStateType } from 'src/pages/DonationSetup/containers/slice';
import { RootState } from 'src/store/store';

/** the donation page state info */
export const donationsPageSavingSelector = ({ donationPage: { saving } }: RootState): DonationPageStateType['saving'] =>
  saving;

export const donationsPageValidationSelector = ({
  donationPage: { validation },
}: RootState): DonationPageStateType['validation'] => validation;

export const donationsPageValidationFormErrorsSelector = ({
  donationPage: { rawValidationFormErrors },
}: RootState): DonationPageStateType['rawValidationFormErrors'] => rawValidationFormErrors;

export const donationsHasPageValidationFormErrorsSelector =
  (name: DonationPageTabsEnum) =>
  ({ donationPage: { rawValidationFormErrors } }: RootState): boolean =>
    (rawValidationFormErrors || []).filter(({ TargetName }) => TargetName === name).length > 0;

export const donationsPagePublishSelector = ({
  donationPage: { publish },
}: RootState): DonationPageStateType['publish'] => publish;

/** the global donation page current section form info */
export const donationsPageCurrentSectionSelector = ({
  donationPage: { currentSectionData },
}: RootState): DonationPageStateType['currentSectionData'] => currentSectionData;

/**
 * first section
 *
 * don't get from here terminology or contribution (there is a helper hook there)
 */
export const deDonationPageFirstFormSectionSelector = ({
  donationPage: { currentSectionData },
}: RootState): DonationPageMainSectionStoreType => {
  invariant(currentSectionData, 'the currentSectionData store is empty (first section)');

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return currentSectionData! as DonationPageMainSectionStoreType;
};

/** second section */
export const deDonationPageSecondFormSectionSelector = ({
  donationPage: { currentSectionData },
}: RootState): DonationPageCheckoutSectionStoreType => {
  invariant(currentSectionData, 'the currentSectionData store is empty (second section)');

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return currentSectionData! as DonationPageCheckoutSectionStoreType;
};

/** third section */
export const deDonationPageThirdFormSectionSelector = ({
  donationPage: { currentSectionData },
}: RootState): DonationPageOptionsSectionStoreType => {
  invariant(currentSectionData, 'the currentSectionData store is empty (third section)');

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return currentSectionData! as DonationPageOptionsSectionStoreType;
};

/** the donation page global data */
export const deDonationsPageGlobalDataSelector = ({
  donationPage: { globalData },
}: RootState): DonationPageGlobalStoreType => {
  invariant(globalData, 'the globalData store is empty');

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return globalData!;
};

/**
 * active form name and a form name which will be next to show
 * returns an object so custom selector is used
 */
export const donationsPageActiveFormNameSelector = createSelector(
  ({ donationPage }: RootState) => donationPage.activeFormName,
  ({ donationPage }: RootState) => donationPage.nextFormName,
  (activeFormName, nextFormName) => ({
    activeFormName,
    nextFormName,
  })
);

/** true if the current section data exists */
export const donationsPageIsDataInCurrentSectionSelector = ({
  donationPage: { currentSectionData },
}: RootState): boolean => currentSectionData !== null;

export const donationPageCurrentTimelineSectionSelector = (state: RootState): DonationPageSectionsType[0] => {
  const {
    timeline: { sections },
  } = deDonationsPageGlobalDataSelector(state);
  const {
    donationPage: { activeFormName },
  } = state;

  const found = sections.find(({ name }) => name === activeFormName);

  invariant(found, "Current section isn't found ");

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return found!;
};

export const donationsPageCausesSelector = ({ donationPage: { causes } }: RootState): DonationPageStateType['causes'] =>
  causes;

export const donationsPageFaqSelector = ({ donationPage: { faq } }: RootState): DonationPageStateType['faq'] => faq;
